// Shared across every app - fetches Pi->USD and USD->local-currency rates,
// caches them for the session, and formats "X Pi ($Y · <symbol>Z)" displays.

const COUNTRY_CURRENCY = {
  'Pakistan': { code: 'PKR', symbol: '₨' },
  'India': { code: 'INR', symbol: '₹' },
  'United States': { code: 'USD', symbol: '$' },
  'United Kingdom': { code: 'GBP', symbol: '£' },
  'United Arab Emirates': { code: 'AED', symbol: 'د.إ' },
  'Saudi Arabia': { code: 'SAR', symbol: '﷼' },
  'Bangladesh': { code: 'BDT', symbol: '৳' },
  'Canada': { code: 'CAD', symbol: 'C$' },
  'Australia': { code: 'AUD', symbol: 'A$' },
  'Qatar': { code: 'QAR', symbol: 'ر.ق' },
  'Kuwait': { code: 'KWD', symbol: 'د.ك' },
  'Bahrain': { code: 'BHD', symbol: '.د.ب' },
  'Oman': { code: 'OMR', symbol: 'ر.ع.' },
  'Malaysia': { code: 'MYR', symbol: 'RM' },
  'Turkey': { code: 'TRY', symbol: '₺' },
  'Germany': { code: 'EUR', symbol: '€' },
  'France': { code: 'EUR', symbol: '€' },
  'Nigeria': { code: 'NGN', symbol: '₦' },
  'Egypt': { code: 'EGP', symbol: 'E£' },
  'Indonesia': { code: 'IDR', symbol: 'Rp' },
  'Philippines': { code: 'PHP', symbol: '₱' },
  'China': { code: 'CNY', symbol: '¥' },
  'Japan': { code: 'JPY', symbol: '¥' },
};

let cachedPiUsdRate = null;
let cachedLocalRates = null;
let cachedCountry = null;
let fetchPromise = null;

async function getUserCountry() {
  if (cachedCountry !== null) return cachedCountry;
  const token = localStorage.getItem('token');
  if (!token) { cachedCountry = ''; return ''; }
  try {
    const res = await fetch('/api/master-profile/', {
      headers: { Authorization: `Token ${token}` },
    });
    const data = await res.json();
    cachedCountry = data.general?.country || '';
  } catch (e) {
    cachedCountry = '';
  }
  return cachedCountry;
}

async function loadRates() {
  if (cachedPiUsdRate !== null && cachedLocalRates !== null) return;
  if (fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    try {
      const res = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=pi-network&vs_currencies=usd');
      const data = await res.json();
      cachedPiUsdRate = data['pi-network']?.usd || 0;
    } catch (e) {
      cachedPiUsdRate = 0;
    }

    try {
      const res2 = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
      const data2 = await res2.json();
      cachedLocalRates = data2.rates || {};
    } catch (e) {
      cachedLocalRates = {};
    }
  })();

  return fetchPromise;
}

/**
 * Returns { usd: "5.00", local: "315", localSymbol: "₨", localCode: "PKR" } | null (null while loading/unavailable)
 */
export async function getPiConversion(piAmount) {
  await loadRates();
  const country = await getUserCountry();

  if (!cachedPiUsdRate) return null;

  const usdValue = piAmount * cachedPiUsdRate;
  const currency = COUNTRY_CURRENCY[country] || COUNTRY_CURRENCY['Pakistan'];

  let localValue = null;
  let localSymbol = null;
  let localCode = null;

  if (currency && cachedLocalRates && cachedLocalRates[currency.code]) {
    localValue = usdValue * cachedLocalRates[currency.code];
    localSymbol = currency.symbol;
    localCode = currency.code;
  }

  return {
    usd: usdValue.toFixed(2),
    local: localValue !== null ? localValue.toLocaleString(undefined, { maximumFractionDigits: 0 }) : null,
    localSymbol, localCode,
  };
}

export async function loadRatesPublic() {
  await loadRates();
  return { piUsd: cachedPiUsdRate, localRates: cachedLocalRates };
}

// Merchant kisi bhi currency me qeemat daale -> Pi + USD nikaalo.
// Payment hamesha Pi me hota hai, is liye price_pi asli hai.
export async function toPiFrom(amount, currencyCode) {
  await loadRates();
  const amt = parseFloat(amount) || 0;
  if (!amt || !cachedPiUsdRate) return null;

  let usd = amt;
  if (currencyCode && currencyCode !== 'USD') {
    const rate = cachedLocalRates && cachedLocalRates[currencyCode];
    if (rate) usd = amt / rate;      // local -> USD
    else return null;
  }
  const pi = usd / cachedPiUsdRate;   // USD -> Pi
  return { pi: pi.toFixed(4), usd: usd.toFixed(2) };
}

// Dropdown ke liye — mulk se code/symbol
export const CURRENCY_LIST = [
  { code: 'PI', symbol: 'π', label: 'Pi' },
  { code: 'USD', symbol: '$', label: 'USD' },
  { code: 'PKR', symbol: '₨', label: 'PKR' },
  { code: 'INR', symbol: '₹', label: 'INR' },
  { code: 'GBP', symbol: '£', label: 'GBP' },
  { code: 'EUR', symbol: '€', label: 'EUR' },
  { code: 'AED', symbol: 'د.إ', label: 'AED' },
  { code: 'SAR', symbol: '﷼', label: 'SAR' },
  { code: 'BDT', symbol: '৳', label: 'BDT' },
  { code: 'IDR', symbol: 'Rp', label: 'IDR' },
  { code: 'MYR', symbol: 'RM', label: 'MYR' },
  { code: 'PHP', symbol: '₱', label: 'PHP' },
  { code: 'NGN', symbol: '₦', label: 'NGN' },
];

export function formatPiConversionText(conversion) {
  if (!conversion) return '';
  let text = `≈ $${conversion.usd}`;
  if (conversion.local !== null) {
    text += ` · ${conversion.localSymbol}${conversion.local}`;
  }
  return text;
}
